import { Badge, Card, Flex, Tag, Typography } from "antd";
import { User } from "../../types/User";
import classNames from "classnames";
import "./UserItem.css";
import Avatar from "../Avatar";
import { RoleLabels } from "../../enums/Roles";
import { Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faCalendarCheck,
  faEnvelope,
  faPencil,
  faPhone,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useCallback } from "react";
import { formatExpiry, getSubscriptionStatus } from "../../utils/subscription";
const { Title } = Typography;

type Props = {
  user: User;
  onEdit?: (user: User) => void;
  onDelete?: (user: User) => void;
  onShow?: (user: User) => void;
};

export default function UserItem({ user, onDelete, onEdit }: Props) {
  const handleEditClick = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      onEdit?.(user);
    },
    [onEdit, user]
  );

  const handleDeleteClick = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      onDelete?.(user);
    },
    [onDelete, user]
  );

  const status = getSubscriptionStatus(user);

  return (
    <Card
      className={classNames("user-item-card", {
        "user-card-active": user.isActive,
        "user-card-inactive": !user.isActive,
      })}
    >
      <Flex align="center" justify="space-between" className="my-4">
        <Flex align="center">
          <Avatar user={user} />
          <Row className="ms-2">
            <Title level={4} className="text-start mb-1">
              {user.nome + " " + user.cognome}
            </Title>
            <div>
              <Badge
                color="blue"
                style={{
                  fontWeight: "bold",
                }}
                className="fw-bold text-uppercase"
                count={RoleLabels[user.role]}
              />
            </div>
          </Row>
        </Flex>
        <Tag color={status.color} className="fw-bold m-0">
          {status.label}
        </Tag>
      </Flex>
      <Flex align="center" justify="flex-start" gap={8}>
        <a href={`mailto:${user.email}`} className="text-muted user-link">
          <FontAwesomeIcon icon={faEnvelope} className="me-2" />
          {user.email}
        </a>
      </Flex>
      <Flex align="center" justify="flex-start" gap={8}>
        {user.phoneNumber ? (
          <a href={`tel:${user.phoneNumber}`} className="text-muted user-link">
            <FontAwesomeIcon icon={faPhone} className="me-2" />
            {user.phoneNumber}
          </a>
        ) : (
          <span className="text-muted">
            <FontAwesomeIcon icon={faPhone} className="me-2" />
            Dati mancanti
          </span>
        )}
      </Flex>
      <Flex align="center" justify="flex-start" gap={8}>
        <span className="text-muted">
          <FontAwesomeIcon icon={faCalendar} className="me-2" />
          {user.birthDate
            ? new Date(user.birthDate).toLocaleDateString("it-IT")
            : "Dati Mancanti"}
        </span>
      </Flex>
      <Flex align="center" justify="flex-start" gap={8}>
        <span
          className={classNames("text-muted", {
            "text-danger": status.key === "expired",
            "text-warning": status.key === "expiring",
          })}
        >
          <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
          Scadenza: {formatExpiry(user.expiring)}
        </span>
      </Flex>
      <hr />
      <Flex align="center" justify="center" gap={12}>
        <button
          className="edit-button edit-button-user"
          onClick={handleEditClick}
        >
          <span className="align-items-center d-flex">
            <FontAwesomeIcon size="sm" icon={faPencil} className="me-2" />
            Modifica
          </span>
        </button>
        <button
          className="edit-button edit-button-user delete-button"
          onClick={handleDeleteClick}
        >
          <span className="align-items-center d-flex">
            <FontAwesomeIcon size="sm" icon={faTrash} className="me-2" />
            Elimina
          </span>
        </button>
      </Flex>
    </Card>
  );
}
